import type { LayoutNode } from '../../components/oasis/bespoke/layout/types';
import {
  buildConsistencyIndexes,
  collectContainerArrangements,
  collectFieldGates,
  collectFieldGroupings,
  collectFieldTreatments,
  NO_GROUPING,
  schemaGeneration,
  type ComparableTemplate,
  type ConsistencyIndex,
  type Variant,
} from './designConsistency';
import { compareFormTypes, mostCanonicalForm } from './formPrecedence';

/**
 * Per-section view of cross-form design consistency (EMR-1410).
 *
 * The comparison is **directed**. For each question, the highest-ranked form
 * rendering it defines the correct treatment (see `formPrecedence`), and a
 * section is flagged only when its own treatment differs from that one. A
 * symmetric "these forms disagree" check would redden every form involved,
 * including the ones that are right, which is why this is not that.
 *
 * Consequences worth knowing:
 *
 * - A section can never be flagged for a question where it is itself the most
 *   canonical form. SOC is right by definition.
 * - Fixing the outlier clears the finding; the conforming forms were never
 *   flagged in the first place.
 * - Only keys the section's own layout uses are consulted, so a section is
 *   never charged with a disagreement about questions it does not contain.
 */

type PropBag = Record<string, unknown>;

/**
 * The caller does not know which QIDs this section's schema defines.
 *
 * Spelled out rather than left as an omitted argument, because the two
 * readings are not interchangeable: an *empty* set says the section defines no
 * questions, which makes both schema-backed suppressions true for everything
 * and turns a drifting section into a clean one. `SCHEMA_UNKNOWN` skips those
 * suppressions instead, so an uninformed caller over-reports rather than
 * silently passing.
 */
export const SCHEMA_UNKNOWN = Symbol('section schema unknown');

/** QIDs a section's schema defines, or `SCHEMA_UNKNOWN` when unavailable. */
export type SectionQids = ReadonlySet<string> | typeof SCHEMA_UNKNOWN;

interface FindingBase {
  /** How this section renders it. */
  current: PropBag;
  /** How the canonical form renders it. */
  canonical: Variant<PropBag>;
  /** The form that defines correct here. */
  canonicalForm: string;
}

export type ConsistencyFinding =
  | (FindingBase & {
      /** This section uses a different control than the canonical form. */
      kind: 'field-control';
      qid: string;
    })
  | (FindingBase & {
      /** Same control, different styling. */
      kind: 'field-props';
      qid: string;
      /** Props that differ from canonical, for a targeted message. */
      differingProps: string[];
      /** True when the only difference is capitalisation or punctuation. */
      caseOnly: boolean;
    })
  | (FindingBase & {
      /** This section arranges a group differently than the canonical form. */
      kind: 'container';
      qids: string[];
    })
  | (FindingBase & {
      /** Same question, revealed on a different condition. */
      kind: 'field-gate';
      qid: string;
    })
  | (FindingBase & {
      /** Same question, but grouped with different company -- or with none. */
      kind: 'field-grouping';
      qid: string;
    });

export type SectionConsistency =
  | { kind: 'unchecked'; reason: string }
  /**
   * The section exists but should not be compared -- its schema pin is not a
   * RogerEMR one. Surfaced as an error rather than hidden, so a WellSky-only
   * pin arriving in future is noticed instead of quietly polluting the index.
   */
  | { kind: 'unrecognised'; reason: string }
  | { kind: 'consistent' }
  | { kind: 'findings'; findings: ConsistencyFinding[] };

/**
 * Using a different control than canonical, or revealing a question on a
 * different condition, is almost certainly a mistake; styling and arrangement
 * differences are more often deliberate.
 */
export function consistencySeverity(
  findings: ConsistencyFinding[]
): 'error' | 'warning' {
  return findings.some(
    finding => finding.kind === 'field-control' || finding.kind === 'field-gate'
  )
    ? 'error'
    : 'warning';
}

/** Prop names whose value differs between two treatments. */
export function differingPropNames(
  current: PropBag,
  canonical: PropBag
): string[] {
  const names = new Set([...Object.keys(current), ...Object.keys(canonical)]);
  return [...names]
    .filter(
      name =>
        JSON.stringify(current[name] ?? null) !==
        JSON.stringify(canonical[name] ?? null)
    )
    .sort((a, b) => a.localeCompare(b));
}

const normalizeText = (value: unknown): string =>
  typeof value === 'string'
    ? value.toLowerCase().replace(/[^a-z0-9]/g, '')
    : '';

/**
 * True when the two say the same words with different capitalisation or
 * punctuation — the subset safe to fix in bulk without judgement.
 */
export function isCaseOnlyDifference(
  current: PropBag,
  canonical: PropBag,
  differing: string[]
): boolean {
  if (differing.length !== 1 || differing[0] !== 'labelOverride') return false;
  const a = normalizeText(current.labelOverride);
  const b = normalizeText(canonical.labelOverride);
  // An absent override normalises to '', which must not collapse into a real
  // label -- "no override at all" renders the schema prompt, a real difference.
  return a === b && a !== '';
}

/** The variant used by the highest-ranked form, or `null` if there is none. */
export function canonicalVariant(
  variants: Variant<PropBag>[]
): { variant: Variant<PropBag>; form: string } | null {
  let best: { variant: Variant<PropBag>; form: string } | null = null;
  for (const variant of variants) {
    const form = mostCanonicalForm(variant.forms);
    if (form === null) continue;
    if (best === null || compareFormTypes(form, best.form) < 0) {
      best = { variant, form };
    }
  }
  return best;
}

/**
 * Compare one treatment against the canonical one.
 *
 * Returns `null` when there is nothing to say: no canonical exists, this form
 * *is* canonical, or it already matches.
 */
function compareToCanonical(
  variants: Variant<PropBag>[] | undefined,
  current: PropBag,
  currentSignature: string,
  formType: string
): FindingBase | null {
  if (!variants || variants.length === 0) return null;
  const canonical = canonicalVariant(variants);
  if (!canonical) return null;
  // This form outranks every other rendering it, so it defines correct.
  // `compareFormTypes` rather than a raw rank: everything unlisted shares one
  // rank, and a raw `<=` would make two unlisted forms each outrank the other,
  // so neither would ever be flagged against the other.
  if (compareFormTypes(formType, canonical.form) <= 0) return null;
  if (currentSignature === canonical.variant.signature) return null;
  return {
    current,
    canonical: canonical.variant,
    canonicalForm: canonical.form,
  };
}

/**
 * Findings for one section's layout, as rendered by `formType`.
 *
 * `formType` matters: it decides whether this section is the authority for a
 * question or is being measured against one. Editing SOC therefore never
 * flags SOC against its own saved layout.
 */
/**
 * True when this form could not express the canonical gate even if it wanted to.
 *
 * A question re-issued under a new id — (M0150) is `m_care_m0150` on SOC and
 * `m_carerequired_m0150` on the REC forms — forces each form to gate its
 * subitem on the id its own schema carries. Comparing the rendered clauses
 * reads that as a disagreement, but there is no layout that settles it: gating
 * on the other form's id would reference a question this schema does not have,
 * which the validator rejects as a stray reference. Only suppressed when the
 * canonical id is genuinely absent here; two forms that both hold both ids and
 * gate differently are still flagged.
 */
function gateIsUnavailable(
  canonical: Variant<PropBag>,
  sectionQids: SectionQids
): boolean {
  if (sectionQids === SCHEMA_UNKNOWN) return false;
  const qids = canonical.detail.gateQids;
  if (!Array.isArray(qids) || qids.length === 0) return false;
  return qids.some(qid => typeof qid === 'string' && !sectionQids.has(qid));
}

/**
 * True when this form has nobody to group the question with.
 *
 * A grouping is a decision about which questions sit together, so a form whose
 * schema holds none of the canonical group's other questions cannot make that
 * decision -- the question stands alone because it is alone. Pruned sections
 * hit this constantly: (M2005) is one of eight medication questions on SOC and
 * the only one on DD, and no layout of DD could group it with the other seven.
 *
 * Only suppressed when *every* other member is missing. A form holding some of
 * them chose to split the group, which is exactly the drift worth reporting.
 */
function groupingIsUnavailable(
  canonical: Variant<PropBag>,
  qid: string,
  sectionQids: SectionQids
): boolean {
  if (sectionQids === SCHEMA_UNKNOWN) return false;
  const group = canonical.detail.groupQids;
  if (!Array.isArray(group)) return false;
  const others = group.filter(
    (member): member is string => typeof member === 'string' && member !== qid
  );
  return others.length > 0 && others.every(member => !sectionQids.has(member));
}

/**
 * True when the canonical form has nobody to group the question with either.
 *
 * The mirror of `groupingIsUnavailable`: there the flagged form cannot make
 * the canonical's grouping, here the canonical could not make this form's.
 * (M2005)-style pruning cuts both ways -- SNAE holds three patient-tracking
 * questions that DC holds one of, so DC leaves it alone for want of company
 * and SNAE is charged with grouping it.
 *
 * The canonical form's schema is not available here, but the index knows which
 * forms render each question, which answers the same thing: a form that never
 * renders the other members could not have grouped them.
 */
function canonicalCouldNotGroup(
  index: ConsistencyIndex,
  canonicalForm: string,
  qid: string,
  current: PropBag
): boolean {
  const group = current.groupQids;
  if (!Array.isArray(group)) return false;
  const others = group.filter(
    (member): member is string => typeof member === 'string' && member !== qid
  );
  if (others.length === 0) return false;
  return others.every(member =>
    (index.fields.get(member) ?? []).every(
      variant => !variant.forms.includes(canonicalForm)
    )
  );
}

export function findSectionConsistency(
  layout: LayoutNode[],
  index: ConsistencyIndex | null,
  formType: string,
  /**
   * QIDs this section's own schema defines, or `SCHEMA_UNKNOWN`.
   *
   * Required: two suppressions read it to tell a real disagreement from one
   * the form could not avoid, so which value is passed changes the verdict.
   * Making the unknown case name itself keeps that choice visible at the call
   * site instead of hiding in an omitted argument.
   */
  sectionQids: SectionQids
): SectionConsistency {
  if (!index) {
    return { kind: 'unchecked', reason: 'template catalog not loaded' };
  }

  const findings: ConsistencyFinding[] = [];

  for (const treatment of collectFieldTreatments(layout).values()) {
    const base = compareToCanonical(
      index.fields.get(treatment.qid),
      treatment.props,
      treatment.signature,
      formType
    );
    if (!base) continue;

    if (base.current.type !== base.canonical.detail.type) {
      findings.push({ ...base, kind: 'field-control', qid: treatment.qid });
      continue;
    }
    const differingProps = differingPropNames(
      base.current,
      base.canonical.detail
    );
    // A signature can differ while no property name does -- an explicit
    // `null` against an omitted key. Reporting that gives a reader a finding
    // that names nothing to change, so say nothing instead.
    if (differingProps.length === 0) continue;
    findings.push({
      ...base,
      kind: 'field-props',
      qid: treatment.qid,
      differingProps,
      caseOnly: isCaseOnlyDifference(
        base.current,
        base.canonical.detail,
        differingProps
      ),
    });
  }

  for (const gate of collectFieldGates(layout).values()) {
    const base = compareToCanonical(
      index.gates.get(gate.qid),
      { gates: gate.gates },
      gate.signature,
      formType
    );
    if (base && !gateIsUnavailable(base.canonical, sectionQids)) {
      findings.push({ ...base, kind: 'field-gate', qid: gate.qid });
    }
  }

  for (const grouping of collectFieldGroupings(layout).values()) {
    const base = compareToCanonical(
      index.groupings.get(grouping.qid),
      grouping.detail,
      grouping.signature,
      formType
    );
    if (
      base &&
      !groupingIsUnavailable(base.canonical, grouping.qid, sectionQids) &&
      !canonicalCouldNotGroup(
        index,
        base.canonicalForm,
        grouping.qid,
        grouping.detail
      )
    ) {
      findings.push({ ...base, kind: 'field-grouping', qid: grouping.qid });
    }
  }

  for (const arrangement of collectContainerArrangements(layout).values()) {
    const base = compareToCanonical(
      index.containers.get(arrangement.contentsKey),
      arrangement.shape,
      arrangement.signature,
      formType
    );
    if (base) {
      findings.push({ ...base, kind: 'container', qids: arrangement.qids });
    }
  }

  if (findings.length === 0) return { kind: 'consistent' };

  // Control and gate mismatches first: unlike styling drift, they change what
  // the clinician can answer, so they are the ones that are likely bugs.
  const severity = (finding: ConsistencyFinding) =>
    finding.kind === 'field-control' || finding.kind === 'field-gate' ? 1 : 0;
  findings.sort((a, b) => severity(b) - severity(a));
  return { kind: 'findings', findings };
}

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Short summary for the section marker's tooltip. */
export function describeConsistencyFindings(
  findings: ConsistencyFinding[]
): string {
  const counts = { control: 0, props: 0, gate: 0, grouping: 0, container: 0 };
  for (const finding of findings) {
    if (finding.kind === 'field-control') counts.control += 1;
    else if (finding.kind === 'field-props') counts.props += 1;
    else if (finding.kind === 'field-gate') counts.gate += 1;
    else if (finding.kind === 'field-grouping') counts.grouping += 1;
    else counts.container += 1;
  }

  const parts: string[] = [];
  if (counts.control) {
    parts.push(
      `${plural(counts.control, 'question')} using a different control`
    );
  }
  if (counts.props) {
    parts.push(`${plural(counts.props, 'question')} styled differently`);
  }
  if (counts.gate) {
    parts.push(`${plural(counts.gate, 'question')} gated differently`);
  }
  if (counts.grouping) {
    parts.push(`${plural(counts.grouping, 'question')} grouped differently`);
  }
  if (counts.container) {
    parts.push(`${plural(counts.container, 'group')} arranged differently`);
  }

  const forms = [...new Set(findings.map(finding => finding.canonicalForm))];
  const reference = forms.length === 1 ? forms[0] : 'the canonical forms';
  return `${parts.join(', ')} than ${reference}`;
}

/** The gate list of a `field-gate` finding, as one readable phrase. */
function renderGates(detail: PropBag): string {
  const gates = detail.gates;
  if (!Array.isArray(gates) || gates.length === 0) return 'no condition';
  return gates.join(' and ');
}

function renderProps(props: PropBag, names: string[]): string {
  if (names.length === 0) return 'default';
  return names
    .map(name =>
      props[name] === undefined
        ? `no ${name}`
        : `${name}=${JSON.stringify(props[name])}`
    )
    .join(' ');
}

/** One line describing a finding, for the section editor's list. */
export function formatConsistencyFinding(finding: ConsistencyFinding): string {
  if (finding.kind === 'container') {
    const names = differingPropNames(finding.current, finding.canonical.detail);
    return (
      `${finding.qids.length} questions grouped here are ` +
      `${renderProps(finding.current, names)} but ` +
      `${renderProps(finding.canonical.detail, names)} on ${finding.canonicalForm}`
    );
  }
  if (finding.kind === 'field-control') {
    return (
      `${finding.qid} renders as ${String(finding.current.type)} here but ` +
      `${String(finding.canonical.detail.type)} on ${finding.canonicalForm}`
    );
  }
  if (finding.kind === 'field-grouping') {
    const describe = (bag: PropBag) =>
      bag.grouping === NO_GROUPING
        ? 'on its own'
        : `in a ${String(bag.grouping)}`;
    return (
      `${finding.qid} sits ${describe(finding.current)} here but ` +
      `${describe(finding.canonical.detail)} on ${finding.canonicalForm}`
    );
  }
  if (finding.kind === 'field-gate') {
    return (
      `${finding.qid} shows on ${renderGates(finding.current)} here but ` +
      `${renderGates(finding.canonical.detail)} on ${finding.canonicalForm}`
    );
  }
  const suffix = finding.caseOnly ? ' (capitalisation only)' : '';
  return (
    `${finding.qid} is ${renderProps(finding.current, finding.differingProps)} ` +
    `here but ${renderProps(finding.canonical.detail, finding.differingProps)} ` +
    `on ${finding.canonicalForm}${suffix}`
  );
}

/** A catalog row plus the two fields that decide whether it may be compared. */
export interface ConsistencyCheckableTemplate extends ComparableTemplate {
  status?: string | null;
  is_rogeremr?: boolean | null;
}

/**
 * Indexes built from the templates that are safe to compare against.
 *
 * Live only: comparing against paused or standby rows would report drift no
 * clinician can see. RogerEMR pins only: a WellSky/Kinnser-only pin in the
 * index would quietly decide the canonical treatment for questions it shares,
 * producing findings against layouts nobody can see.
 */
export function isComparablePin(
  template: ConsistencyCheckableTemplate
): boolean {
  return template.status === 'live' && template.is_rogeremr === true;
}

export function buildLiveConsistencyIndexes(
  templates: readonly ConsistencyCheckableTemplate[]
): Map<string, ConsistencyIndex> {
  return buildConsistencyIndexes(templates.filter(isComparablePin));
}

/**
 * Consistency state for one section's template, with the guards every caller
 * needs (EMR-1410).
 *
 * Shared so the section nav and the validation results modal cannot drift into
 * disagreeing about whether a section is consistent — the guards, not the
 * comparison, are where that would happen.
 */
export function sectionConsistencyForTemplate(
  template: ConsistencyCheckableTemplate | undefined,
  indexes: Map<string, ConsistencyIndex>,
  sectionQids: SectionQids
): SectionConsistency {
  const layout = template?.layout_json;
  if (!template || !Array.isArray(layout)) {
    return { kind: 'unchecked', reason: 'no custom layout' };
  }
  // Fail visibly rather than comparing against a catalog we cannot vouch for.
  // `null` means RogerDoc did not answer; `false` means this pin is
  // WellSky-only and should not have a RogerEMR layout at all.
  if (template.is_rogeremr == null) {
    return {
      kind: 'unchecked',
      reason: 'RogerDoc did not confirm which schema pins target RogerEMR',
    };
  }
  if (!template.is_rogeremr) {
    return {
      kind: 'unrecognised',
      reason: `schema pin "${template.schema_version_name}" does not target RogerEMR`,
    };
  }
  return findSectionConsistency(
    layout as LayoutNode[],
    indexes.get(schemaGeneration(template)) ?? null,
    template.form_type,
    sectionQids
  );
}

/**
 * Parent consistency from children. Worst wins:
 * unrecognised > findings (error) > findings (warning) > unchecked > consistent.
 *
 * Mirrors `rollupValidationStatus`, including its rule that nothing-checked is
 * never reported as clean: an empty group is `unchecked`, not `consistent`.
 * Findings are concatenated rather than counted here so the parent's tooltip
 * can describe the whole group the same way a section's does.
 */
export function rollupSectionConsistency(
  sections: readonly SectionConsistency[]
): SectionConsistency {
  if (sections.length === 0) {
    return { kind: 'unchecked', reason: 'no sections checked' };
  }

  const unrecognised = sections.find(s => s.kind === 'unrecognised');
  if (unrecognised) return unrecognised;

  const findings = sections.flatMap(s =>
    s.kind === 'findings' ? s.findings : []
  );
  if (findings.length > 0) return { kind: 'findings', findings };

  const unchecked = sections.find(s => s.kind === 'unchecked');
  if (unchecked) return unchecked;

  return { kind: 'consistent' };
}
